/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import APIClient from 'APIClient';
import Text from 'components/Text';
import Avatar from 'components/Avatar';
import handleError from 'utils/handleError';
import { MEDIA_QUERIES } from 'styles/constants';
import IphoneFeek from 'assets/images/Iphone-Feek.png';
import { BLACK, WHITE, GRAY_BG, PURPLE_LIGHT } from 'styles/colors';

const styles = css`
  background-color: ${GRAY_BG};

  .clients-header {
    max-width: 720px;
    margin: 0 auto;
    padding: 60px 40px 60px;

    ${MEDIA_QUERIES.tablet} {
      max-width: 100%;
      padding: 80px 48px 120px;
      font-size: 36px;
    }

    ${MEDIA_QUERIES.mobile} {
      max-width: 100%;
      padding: 60px 24px 100px;
    }
  }

  .iphone-feek-image {
    position: absolute;
    z-index: 1;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
  }

  .container {
    position: relative;
    min-height: 1000px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .carrousel {
    position: absolute;
    bottom: 200px;
    left: 0;
    right: 0;
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    z-index: 3;
    gap: 40px;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }

    .carrousel-spacer {
      flex-shrink: 0;
      width: calc(50% - 197.5px);
    }

    .feekspotCard {
      background-color: ${WHITE};
      border-radius: 20px;
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
      position: relative;
      width: 315px;
      flex-shrink: 0;
      scroll-snap-align: center;

      .feekspotCardBgImage {
        display: block;
        margin-block-end: 15px;
        width: 315px;
        height: 250px;
        border-radius: 20px 20px 0 0;
        object-fit: cover;
        object-position: center center;
      }

      .feekspotCategoryPill {
        position: absolute;
        top: 20px;
        right: 20px;
        padding: 5px 10px;
        background-color: ${PURPLE_LIGHT};
        border-radius: 16px;
      }

      .feekspotCardFooter {
        padding: 20px;
        display: flex;
        flex-direction: column;
        white-space: normal;
        min-width: 0;

        .feekspotCardLogo {
          display: flex;
          gap: 10px;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          min-width: 0;

          > div {
            min-width: 0;
            overflow: hidden;
          }
        }

        .feekspotCardName,
        .feekspotCardLocation {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .feekspotCardName {
          text-transform: uppercase;
        }
      }
    }
  }
`;

function FeekspotsCarrousel() {
  const [feekspots, setFeekspots] = useState(Array(4));

  const containerRef = useRef(null);
  const dataLengthRef = useRef(0);

  useEffect(() => {
    APIClient.getLandingSelectedFeekspots()
      .then(({ data }) => {
        dataLengthRef.current = data.length;
        setFeekspots([
          ...data,
          ...data,
          ...data,
          ...data,
          ...data,
          ...data,
          ...data,
        ]);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollLeft =
                data.length * 3 * (315 + 40);
            }
          }),
        );
      })
      .catch(handleError);

    const container = containerRef.current;

    const CARD_STEP = 315 + 40;
    let isScrolling = false;
    let wheelEndTimer = null;

    const advance = () => {
      isScrolling = true;
      container.scrollBy({ left: CARD_STEP, behavior: 'smooth' });
      clearTimeout(wheelEndTimer);
      wheelEndTimer = setTimeout(() => {
        isScrolling = false;
      }, 150);
    };

    let autoTimer = setInterval(advance, 7000);

    const resetAutoTimer = () => {
      clearInterval(autoTimer);
      autoTimer = setInterval(advance, 7000);
    };

    const handleWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();

      resetAutoTimer();

      clearTimeout(wheelEndTimer);
      wheelEndTimer = setTimeout(() => {
        isScrolling = false;
      }, 150);

      if (isScrolling) return;
      isScrolling = true;
      container.scrollBy({
        left: e.deltaY > 0 ? CARD_STEP : -CARD_STEP,
        behavior: 'smooth',
      });
    };

    const handleScrollEnd = () => {
      const n = dataLengthRef.current;
      if (!n) return;
      const cycleWidth = n * CARD_STEP;
      if (container.scrollLeft >= 5 * cycleWidth) {
        container.scrollLeft -= 3 * cycleWidth;
      } else if (container.scrollLeft <= 2 * cycleWidth) {
        container.scrollLeft += 3 * cycleWidth;
      }
    };

    container.addEventListener('wheel', handleWheel, {
      passive: false,
    });
    container.addEventListener('scrollend', handleScrollEnd);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scrollend', handleScrollEnd);
      clearTimeout(wheelEndTimer);
      clearInterval(autoTimer);
    };
  }, []);

  return (
    <div css={styles}>
      <div className="clients-header">
        <Text
          fontWeight={700}
          fontSize={48}
          fontSizeMobile={32}
          lineHeight={57.6}
          align="center"
          css={css`
            color: #5e48ff;
            leading-trim: both;
            text-edge: cap;
          `}
        >
          Clientes que confían en Feek
        </Text>
        <Text
          fontWeight={400}
          fontSize={28}
          fontSizeMobile={20}
          lineHeight={40.6}
          align="center"
          className="landing-title"
          css={css`
            color: #313033;
            leading-trim: both;
            text-edge: cap;
          `}
        >
          Estos son algunos de los negocios que han decidido confiar
          en <b>Feek</b>.
        </Text>
      </div>
      <div className="container">
        <img
          src={IphoneFeek}
          alt="Feekspots"
          className="iphone-feek-image"
        />
        <div ref={containerRef} className="carrousel">
          <div className="carrousel-spacer" aria-hidden="true" />
          {feekspots?.map((feekspot, index) => (
            <div
              key={`${feekspot?._id}-${index}`}
              className="feekspotCard"
            >
              <img
                className="feekspotCardBgImage"
                src={feekspot?.background_image}
                alt={'Feek ' + feekspot?.name}
              />

              <footer className="feekspotCardFooter">
                <div className="feekspotCardLogo">
                  <Avatar
                    className=""
                    image={feekspot?.image}
                    size={60}
                    sizeMobile={60}
                    name={feekspot?.name}
                    alt={'Feek ' + feekspot?.name}
                  />
                  <div>
                    <Text
                      className="feekspotCardName"
                      fontSize={20}
                      fontWeight={700}
                      color={BLACK}
                    >
                      {feekspot?.name}
                    </Text>
                    <Text
                      className="feekspotCardLocation"
                      fontSize={14}
                      fontWeight={500}
                      color={BLACK}
                    >
                      {feekspot?.location?.city},{' '}
                      {feekspot?.location?.state}
                    </Text>
                  </div>
                </div>
              </footer>
            </div>
          ))}
          <div className="carrousel-spacer" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

export default FeekspotsCarrousel;
